import { createFileRoute } from '@tanstack/react-router'
import getSymbolFromCurrency from 'currency-symbol-map'
import { Trash2 } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { useCreatePurchaseOrder } from '@/api/api-mutations'
import { useGetDashboardFeatures, useListInventoryLocations, useListSuppliers } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { CurrencyInput } from '@/components/base/currency-input'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Text } from '@/components/base/text'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { getCurrencyDecimals, toPrecision } from '@/utils/currency'

import { useVariantLineDraft, VariantSearchInput, type VariantDraftLine } from '../-components'
import { getCurrencyOptions, resolvePurchaseOrderCurrency, type PurchaseCurrencySource } from '../-currency-utils'

type DraftLine = VariantDraftLine & { unitCost: string }

export const Route = createFileRoute('/_authenticated/_general/inventory/receiving/create')({
	component: CreatePurchaseOrderRoute,
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.CreatePurchaseOrder()
	}
})

function CreatePurchaseOrderRoute() {
	return (
		<RouteModal asDrawer prev='..'>
			<CreatePurchaseOrderForm />
		</RouteModal>
	)
}

function CreatePurchaseOrderForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()
	const createPo = useCreatePurchaseOrder(true)
	const dashboardFeatures = useGetDashboardFeatures()
	const inventoryLocations = useListInventoryLocations({ limit: 100, offset: 0 })
	const suppliers = useListSuppliers({ limit: 100, offset: 0 })
	const linesRef = useRef<DraftLine[]>([])
	const tenantDefaultCurrencyCode = dashboardFeatures.data?.reportingCurrencyCode

	const form = useAppForm({
		defaultValues: {
			locationUuid: '',
			supplierUuid: '',
			reference: '',
			currencyCode: ''
		},
		onSubmit: async ({ value }) => {
			const lines = linesRef.current
			if (lines.length === 0) return
			if (!value.currencyCode) {
				toast.error(L.General.Required())
				return
			}
			const currencyCode = value.currencyCode
			const currencyDecimals = getCurrencyDecimals(currencyCode)

			const result = await createPo.mutateAsync({
				body: {
					inventoryLocationUuid: value.locationUuid,
					supplierUuid: value.supplierUuid || undefined,
					currencyCode,
					referenceNumber: value.reference || undefined,
					lines: lines.map(line => ({
						variantUuid: line.variantUuid,
						orderedQuantity: Number(line.quantity),
						unitCost:
							line.unitCost === '' ? undefined : toPrecision(Number(line.unitCost), currencyDecimals)
					}))
				}
			})
			createdToast(L.Inventory.CreatePurchaseOrder(), {
				to: '/inventory/receiving/$purchaseOrderUuid',
				params: { purchaseOrderUuid: result.response.uuid }
			})
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.CreatePurchaseOrder()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
							<form.AppField
								name='locationUuid'
								validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
								children={field => (
									<field.SelectField
										label={L.Inventory.Column.InventoryLocation()}
										options={(inventoryLocations.data?.inventoryLocations ?? []).map(location => ({
											value: location.uuid,
											label: location.name
										}))}
									/>
								)}
							/>
							<form.AppField
								name='supplierUuid'
								children={field => (
									<field.SelectField
										label={L.Inventory.Suppliers()}
										options={(suppliers.data?.suppliers ?? []).map(supplier => ({
											value: supplier.uuid,
											label: supplier.name
										}))}
									/>
								)}
							/>
							<form.AppField
								name='reference'
								children={field => <field.TextField label={L.Inventory.Column.Reference()} optional />}
							/>
						</div>

						<PurchaseOrderCurrencySection
							form={form}
							suppliers={suppliers.data?.suppliers ?? []}
							inventoryLocations={inventoryLocations.data?.inventoryLocations ?? []}
							tenantDefaultCurrencyCode={tenantDefaultCurrencyCode}
							onLinesChange={lines => {
								linesRef.current = lines
							}}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small' type='button'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}

function PurchaseOrderCurrencySection({
	form,
	suppliers,
	inventoryLocations,
	tenantDefaultCurrencyCode,
	onLinesChange
}: {
	form: any
	suppliers: Array<{ uuid: string; currencyCode: string }>
	inventoryLocations: Array<{ uuid: string; currencyCode?: string }>
	tenantDefaultCurrencyCode: string | undefined
	onLinesChange: (lines: DraftLine[]) => void
}) {
	return (
		<form.Subscribe
			selector={state => ({
				locationUuid: state.values.locationUuid,
				supplierUuid: state.values.supplierUuid,
				currencyCode: state.values.currencyCode
			})}
		>
			{({ locationUuid, supplierUuid, currencyCode }) => {
				const suggestion = resolvePurchaseOrderCurrency({
					supplierCurrencyCode: suppliers.find(supplier => supplier.uuid === supplierUuid)?.currencyCode,
					// Only set when the location's sales channels agree on one currency (resolved
					// server-side); ambiguous locations leave it unset rather than guessing a channel.
					locationCurrencyCode: inventoryLocations.find(location => location.uuid === locationUuid)
						?.currencyCode,
					tenantDefaultCurrencyCode
				})

				return (
					<PurchaseOrderCurrencySectionBody
						form={form}
						locationUuid={locationUuid}
						currencyCode={currencyCode}
						suggestedCurrencyCode={suggestion?.currencyCode}
						suggestionSource={suggestion?.source}
						onLinesChange={onLinesChange}
					/>
				)
			}}
		</form.Subscribe>
	)
}

function PurchaseOrderCurrencySectionBody({
	form,
	locationUuid,
	currencyCode,
	suggestedCurrencyCode,
	suggestionSource,
	onLinesChange
}: {
	form: any
	locationUuid: string
	currencyCode: string
	suggestedCurrencyCode: string | undefined
	suggestionSource: PurchaseCurrencySource | undefined
	onLinesChange: (lines: DraftLine[]) => void
}) {
	const { L, locale } = useI18n()

	// Only prefill the suggested currency while the user hasn't picked their own. We treat the field
	// as untouched when it's empty or still equals the last suggestion we applied, so changing the
	// location/supplier updates the prefill but never clobbers a manual selection.
	const lastAppliedSuggestion = useRef<string | undefined>(undefined)
	useEffect(() => {
		if (!suggestedCurrencyCode) return
		const untouched = currencyCode === '' || currencyCode === lastAppliedSuggestion.current
		if (untouched && currencyCode !== suggestedCurrencyCode) {
			form.setFieldValue('currencyCode', suggestedCurrencyCode)
		}
		lastAppliedSuggestion.current = suggestedCurrencyCode
	}, [form, suggestedCurrencyCode, currencyCode])

	return (
		<>
			<div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
				<form.AppField
					name='currencyCode'
					validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
					children={field => (
						<field.SelectField label={L.Inventory.Column.Currency()} options={getCurrencyOptions(locale)} />
					)}
				/>
				<Text className='self-end pb-2 text-sm text-ui-fg-subtle'>
					{!suggestedCurrencyCode
						? L.Inventory.PurchaseCurrencyRequired()
						: suggestionSource === 'tenant'
							? L.Inventory.PurchaseCurrencyDefaultHint({ currency: suggestedCurrencyCode })
							: L.Inventory.PurchaseCurrencyHint({ currency: suggestedCurrencyCode })}
				</Text>
			</div>
			<PurchaseOrderLinesEditor
				locationUuid={locationUuid}
				currencyCode={currencyCode || suggestedCurrencyCode || '—'}
				onLinesChange={onLinesChange}
			/>
		</>
	)
}

function PurchaseOrderLinesEditor({
	locationUuid,
	currencyCode,
	onLinesChange
}: {
	locationUuid: string
	currencyCode: string
	onLinesChange: (lines: DraftLine[]) => void
}) {
	const { L } = useI18n()
	const { search, setSearch, lines, addLine, updateLine, removeLine, variantResults } =
		useVariantLineDraft<DraftLine>({
			active: true,
			inventoryLocationUuid: locationUuid,
			createLine: base => ({ ...base, unitCost: '' })
		})

	useEffect(() => {
		onLinesChange(lines)
	}, [lines, onLinesChange])

	return (
		<>
			<VariantSearchInput
				search={search}
				setSearch={setSearch}
				disabled={locationUuid === ''}
				results={variantResults.data?.variants ?? []}
				onSelect={variant => addLine(variant.uuid, variant.productTitle, variant.sku)}
			/>
			<div className='flex flex-col gap-2'>
				{lines.length === 0 && <Text className='text-sm text-ui-fg-muted'>{L.Inventory.NoLines()}</Text>}
				{lines.map(line => (
					<div key={line.variantUuid} className='grid grid-cols-12 items-center gap-2 text-sm'>
						<div className='col-span-5 min-w-0'>
							<div className='truncate'>{line.productTitle}</div>
							{line.sku && <div className='truncate text-xs text-ui-fg-muted'>{line.sku}</div>}
						</div>
						<div className='col-span-3'>
							<Input
								type='number'
								step='any'
								placeholder={L.Inventory.Column.Quantity()}
								value={line.quantity}
								onChange={e => updateLine(line.variantUuid, { quantity: e.target.value })}
							/>
						</div>
						<div className='col-span-3'>
							<CurrencyInput
								code={currencyCode}
								symbol={getSymbolFromCurrency(currencyCode) ?? currencyCode}
								placeholder={L.Inventory.Column.UnitCost()}
								value={line.unitCost}
								onValueChange={value => updateLine(line.variantUuid, { unitCost: value ?? '' })}
							/>
						</div>
						<div className='col-span-1 flex justify-end'>
							<IconButton
								size='small'
								variant='transparent'
								tooltipText={L.General.Delete()}
								onClick={() => removeLine(line.variantUuid)}
							>
								<Trash2 className='size-4' />
							</IconButton>
						</div>
					</div>
				))}
			</div>
		</>
	)
}
